import React, { useState, useEffect } from "react";
import Loading from "../../Common/Loading";

import popup_bg1 from "../../Common/Images/ImagesForAlert/popup_bg1.png";
import alert_close_n from "../../Common/Images/ImagesForAlert/alert_close_n.png";
import alert_close_o from "../../Common/Images/ImagesForAlert/alert_close_o.png";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface Props {
  compId: number | null;
  onClose: () => void;
  onSaveSuccess: () => void;
  showWarning: (msg: string) => void;
  showConfirm: (msg: string, onConfirm: () => void) => void;
}

interface Dealer {
  Name: string;
  SchoolCode: string;
  Country: string;
}

const EL_CompARD: React.FC<Props> = ({
  compId,
  onClose,
  onSaveSuccess,
  showWarning,
  showConfirm,
}) => {
  const isEditMode = compId !== null;
  const [loading, setLoading] = useState(false);

  const [images, setImages] = useState<string[]>([]);
  const [allCountries, setAllCountries] = useState<string[]>([]);

  const [title, setTitle] = useState("");
  const [compType, setCompType] = useState<"Regular" | "Grand">("Regular");
  const [price, setPrice] = useState<number>(0);
  const [noOfWinners, setNoOfWinners] = useState<number>(1);

  const [cluePrice, setCluePrice] = useState<number>(0);
  const [clues, setClues] = useState<string[]>(["", "", "", "", "", ""]);
  const [answer, setAnswer] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");

  const [selectedImage, setSelectedImage] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  const [terms, setTerms] = useState("");

  const [allocSchoolsChecked, setAllocSchoolsChecked] = useState(false);
  const [allocHomesChecked, setAllocHomesChecked] = useState(false);

  const [selectedSchoolCountries, setSelectedSchoolCountries] = useState<
    string[]
  >([]);
  const [selectedHomeCountries, setSelectedHomeCountries] = useState<string[]>(
    [],
  );

  const [dealersList, setDealersList] = useState<Dealer[]>([]);
  const [selectedDealerCodes, setSelectedDealerCodes] = useState<string[]>([]);

  const [isEnded, setIsEnded] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedImage) {
      fetchSasUrl(selectedImage);
    } else {
      setPreviewUrl("");
    }
  }, [selectedImage]);

  const fetchSasUrl = async (imgName: string) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}api/manage-comps/get_image_url/?image_name=${imgName}`,
      );
      if (res.ok) {
        const data = await res.json();
        setPreviewUrl(data.url);
      } else {
        setPreviewUrl("");
      }
    } catch (e) {
      console.error(e);
      setPreviewUrl("");
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const imgRes = await fetch(`${API_BASE_URL}api/manage-comps/images/`);
      if (imgRes.ok) setImages(await imgRes.json());

      const cRes = await fetch(`${API_BASE_URL}api/manage-comps/countries/`);
      if (cRes.ok) setAllCountries(await cRes.json());

      if (isEditMode && compId) {
        const detRes = await fetch(
          `${API_BASE_URL}api/manage-comps/${compId}/`,
        );
        if (detRes.ok) {
          const data = await detRes.json();
          await populateForm(data);
        }
      } else {
        const today = new Date().toISOString().split("T")[0];
        setStartDate(today);
        setEndDate(today);
      }
    } catch (error) {
      console.error(error);
      showWarning("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const populateForm = async (data: any) => {
    const c = data.comp;
    const alloc = data.allocations;

    setTitle(c.CompTitle);
    setCompType(c.Type === "Grand" ? "Grand" : "Regular");
    setPrice(c.Price);
    setSelectedImage(c.CompImage);

    if (c.StartDate) setStartDate(c.StartDate.split("T")[0]);
    if (c.ClosesOn) {
      const dt = new Date(c.ClosesOn);
      setEndDate(dt.toISOString().split("T")[0]);
      setEndTime(dt.toTimeString().substring(0, 5));
      const now = new Date();
      setIsEnded(dt < now);
    }

    setTerms(c.Terms);

    if (c.Type === "Regular") {
      setNoOfWinners(c.NoOfWinners || 1);
    } else {
      setCluePrice(c.CluePrice || 0);
      setClues([
        c.Clue1 || "",
        c.Clue2 || "",
        c.Clue3 || "",
        c.Clue4 || "",
        c.Clue5 || "",
        c.Clue6 || "",
      ]);
      setAnswer(c.Answer || "");
    }

    if (alloc.Schools && alloc.Schools.length > 0) {
      setAllocSchoolsChecked(true);
      setSelectedSchoolCountries(alloc.Schools);
    }

    if (alloc.Homes && alloc.Homes.length > 0) {
      setAllocHomesChecked(true);

      const homeCountries = Array.from(
        new Set(alloc.Homes.map((h: any) => h.Country)),
      ) as string[];
      setSelectedHomeCountries(homeCountries);

      const selectedCodes = alloc.Homes.map((h: any) => h.SchoolCode);
      setSelectedDealerCodes(selectedCodes);

      for (const country of homeCountries) {
        await fetchAndAppendDealers(country);
      }
    }
  };

  const toggleHomeCountry = async (country: string) => {
    if (!allocHomesChecked) return;

    const isChecked = selectedHomeCountries.includes(country);

    if (isChecked) {
      setSelectedHomeCountries((prev) => prev.filter((c) => c !== country));
      setDealersList((prev) => prev.filter((d) => d.Country !== country));
    } else {
      setSelectedHomeCountries((prev) => [...prev, country]);
      await fetchAndAppendDealers(country);
    }
  };

  const fetchAndAppendDealers = async (country: string) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}api/manage-comps/dealers/?country=${country}`,
      );
      if (res.ok) {
        const newDealers: Dealer[] = await res.json();
        const taggedDealers = newDealers.map((d) => ({
          ...d,
          Country: country,
        }));

        setDealersList((prev) => {
          const existingCodes = new Set(prev.map((p) => p.SchoolCode));
          const uniqueNew = taggedDealers.filter(
            (d) => !existingCodes.has(d.SchoolCode),
          );
          return [...prev, ...uniqueNew];
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleDealerSelection = (code: string) => {
    if (!allocHomesChecked) return;
    setSelectedDealerCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleSchoolCountry = (country: string) => {
    if (!allocSchoolsChecked) return;
    setSelectedSchoolCountries((prev) =>
      prev.includes(country)
        ? prev.filter((c) => c !== country)
        : [...prev, country],
    );
  };

  const handleSave = async () => {
    if (!title) {
      showWarning("Please enter a title");
      return;
    }

    if (!endTime) {
      showWarning("Please enter the time");
      return;
    }

    if (!price) {
      showWarning("Please enter the price");
      return;
    }

    if (!selectedImage) {
      showWarning("Please select an image");
      return;
    }

    if (!allocSchoolsChecked && !allocHomesChecked) {
      showWarning("Please select Allocated To");
      return;
    }

    const allocatedFlag =
      allocSchoolsChecked && allocHomesChecked
        ? "A"
        : allocSchoolsChecked
          ? "S"
          : allocHomesChecked
            ? "F"
            : "";

    const schoolsPayload = allocSchoolsChecked ? selectedSchoolCountries : [];

    const homesPayload = [];
    if (allocHomesChecked) {
      const validDealers = dealersList.filter((d) =>
        selectedDealerCodes.includes(d.SchoolCode),
      );
      for (const d of validDealers) {
        homesPayload.push({ Country: d.Country, SchoolCode: d.SchoolCode });
      }
    }

    if (allocSchoolsChecked && schoolsPayload.length === 0) {
      showWarning("Please select Allocated To");
      return;
    }

    if (!terms) {
      showWarning("Please enter Terms & Condition");
      return;
    }

    const payload = {
      Title: title,
      Type: compType,
      Price: price,
      CompImage: selectedImage,
      StartDate: startDate,
      EndDate: endDate,
      EndTime: endTime,
      Terms: terms,
      AllocatedToFlag: allocatedFlag,
      AllocatedSchools: schoolsPayload,
      AllocatedHomes: homesPayload,
      ...(compType === "Regular"
        ? { NoOfWinners: noOfWinners }
        : {
            CluePrice: cluePrice,
            Answer: answer,
            Clue1: clues[0],
            Clue2: clues[1],
            Clue3: clues[2],
            Clue4: clues[3],
            Clue5: clues[4],
            Clue6: clues[5],
          }),
    };

    const url = isEditMode
      ? `${API_BASE_URL}api/manage-comps/${compId}/`
      : `${API_BASE_URL}api/manage-comps/`;

    const method = isEditMode ? "PUT" : "POST";
    const msg = isEditMode ? "update" : "add";

    showConfirm(`Do you want to ${msg} this competition?`, async () => {
      setLoading(true);
      try {
        const res = await fetch(url, {
          method: method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          onSaveSuccess();
          onClose();
        } else {
          const err = await res.json();
          showWarning(err.error || "Error saving competition");
        }
      } catch (e) {
        console.error(e);
        showWarning("Network Error");
      } finally {
        setLoading(false);
      }
    });
  };

  const handleCancel = () => {
    if (!isEditMode) {
      showConfirm("Do you want to close the window?", () => {
        onClose();
      });
    } else {
      onClose();
    }
  };

  const styles = {
    overlay: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 9999,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    modal: {
      backgroundColor: "#f0ffff",
      width: "100%",
      height: "100%",
      maxHeight: "100vh",
      display: "flex",
      flexDirection: "column" as const,
      border: "none",
      overflow: "hidden",
      fontFamily: "Arial, sans-serif",
      fontSize: "11pt",
    },
    popupHeader: {
      backgroundImage: `url(${popup_bg1})`,
      backgroundRepeat: "repeat-x",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid #000",
      height: "30px",
      color: "white",
      fontFamily: "Calibri, Arial, sans-serif",
      fontSize: "11pt",
      fontWeight: "bold" as const,
      padding: "0 10px",
    },
    wrapper98: {
      width: "98%",
      margin: "0 auto",
      display: "flex",
      flexDirection: "column" as const,
      flex: 1,
      overflow: "hidden",
    },
    maroonHeader: {
      marginTop: "5px",
      backgroundColor: "#800000",
      color: "white",
      padding: "5px",
      textAlign: "center" as const,
      fontWeight: "bold" as const,
      fontSize: "11pt",
      width: "100%",
      marginBottom: "5px",
    },
    contentBox: {
      border: "1px solid #82A0F6",
      backgroundColor: "#f0ffff",
      display: "flex",
      flex: 1,
      overflow: "hidden",
    },
    leftPanel: {
      flex: "0 0 58%",
      padding: "2px",
      borderRight: "1px solid #82A0F6",
      overflowY: "auto" as const,
      boxSizing: "border-box" as const,
    },
    rightPanel: {
      flex: "0 0 42%",
      padding: "2px",
      overflowY: "auto" as const,
      display: "flex",
      flexDirection: "column" as const,
      boxSizing: "border-box" as const,
      minWidth: 0,
      alignItems: "stretch" as const,
    },
    sectionHeader: {
      backgroundColor: "#f0ffff",
      padding: "5px",
      borderBottom: "1px solid #80A0FF",
      fontWeight: "bold" as const,
      fontSize: "11pt",
    },
    allocHeader: {
      backgroundColor: "#ECFACB",
      fontWeight: "bold" as const,
      borderBottom: "1px solid #82A0F6",
      marginBottom: "5px",
    },
    allocationSection: {
      display: "flex",
      flexDirection: "column" as const,
      width: "100%",
      alignItems: "stretch" as const,
    //   border: "1px solid #82A0F6",
    //   backgroundColor: "white",
      marginBottom: "10px",
      overflow: "hidden" as const,
    },
    scrollTable: {
      width: "100%",
      maxWidth: "100%",
      border: "1px solid #82A0F6",
      height: "130px",
      overflowY: "auto" as const,
    //   backgroundColor: "white",
      marginBottom: "10px",
      boxSizing: "border-box" as const,
    },
    gridRow: (idx: number) => ({
      backgroundColor: idx % 2 === 0 ? "#F4FAFF" : "#FFFFFF",
      padding: "2px",
      fontSize: "9pt",
      borderBottom: "1px solid #E0E0E0",
    }),
    gridHeader: {
      backgroundColor: "#C7DDFD",
      fontWeight: "bold" as const,
      padding: "2px",
      borderBottom: "1px solid #82A0F6",
      borderTop: "1px solid #82A0F6",
    },
    footer: {
      textAlign: "center" as const,
      padding: "10px",
      width: "100%",
    },
    footerBtn: {
      width: "100px",
      height: "25px",
      cursor: "pointer",
      margin: "0 10px",
    },
    input: {
      border: "1px solid #80A0FF",
      padding: "2px",
      fontFamily: "Arial",
      fontSize: "9pt",
    },
    label: {
      display: "inline-block",
      width: "130px",
      paddingLeft: "12px",
      fontWeight: "normal" as const,
    },
    row: { marginBottom: "8px", display: "flex", alignItems: "center" },
    warningBanner: {
      color: "red",
      fontWeight: "bold" as const,
      marginLeft: "10px",
    },
  };

  if (loading) return <Loading />;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.popupHeader}>
          <span>Add Edit Comps</span>
          <img
            src={alert_close_n}
            alt="Close"
            onClick={handleCancel}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) => (e.currentTarget.src = alert_close_o)}
            onMouseOut={(e) => (e.currentTarget.src = alert_close_n)}
          />
        </div>

        <div style={styles.wrapper98}>
          <div style={styles.maroonHeader}>
            {isEditMode ? "Modify Comp" : "Add New Comp"}
          </div>

          <div style={styles.contentBox}>
            <div style={styles.leftPanel}>
              <div style={styles.sectionHeader}>
                {isEditMode ? "View/Modify Competition" : "Add New Competition"}
                {isEnded && (
                  <span style={styles.warningBanner}>
                    This competition has been ended and cannot be modified.
                  </span>
                )}
              </div>
              <div style={{ padding: "10px" }}>
                <div style={styles.row}>
                  <span style={styles.label}>Type</span>
                  <label>
                    <input
                      type="radio"
                      checked={compType === "Regular"}
                      onChange={() => setCompType("Regular")}
                      disabled={isEnded}
                    />{" "}
                    Regular
                  </label>
                  <label style={{ marginLeft: "10px" }}>
                    <input
                      type="radio"
                      checked={compType === "Grand"}
                      onChange={() => setCompType("Grand")}
                      disabled={isEnded}
                    />{" "}
                    Grand
                  </label>
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Title (Max Char. 50)</span>
                  <input
                    style={{ ...styles.input, width: "200px" }}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={50}
                    disabled={isEnded}
                  />
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Price (Max 999999)</span>
                  <input
                    style={{ ...styles.input, width: "100px" }}
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(parseInt(e.target.value))}
                    disabled={isEnded}
                  />
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Start Date (GMT)</span>
                  <input
                    style={{ ...styles.input, width: "130px" }}
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={isEnded}
                  />
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Entry Closes (GMT)</span>
                  <input
                    style={{ ...styles.input, width: "130px" }}
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={isEnded}
                  />
                  <input
                    style={{
                      ...styles.input,
                      width: "53px",
                      marginLeft: "5px",
                    }}
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    disabled={isEnded}
                  />
                  <span
                    style={{
                      fontStyle: "italic",
                      fontSize: "8pt",
                      marginLeft: "5px",
                    }}
                  >
                    time hh:mm
                  </span>
                </div>

                {compType === "Regular" ? (
                  <div style={styles.row}>
                    <span style={styles.label}>No. of winner/s</span>
                    <input
                      style={{ ...styles.input, width: "100px" }}
                      type="number"
                      value={noOfWinners}
                      onChange={(e) => setNoOfWinners(parseInt(e.target.value))}
                      disabled={isEnded}
                    />
                  </div>
                ) : (
                  <>
                    <div style={styles.row}>
                      <span style={styles.label}>Clue Price</span>
                      <input
                        style={{ ...styles.input, width: "100px" }}
                        type="number"
                        value={cluePrice}
                        onChange={(e) => setCluePrice(parseInt(e.target.value))}
                        disabled={isEnded}
                      />
                    </div>
                    {clues.map((clue, i) => (
                      <div key={i} style={styles.row}>
                        <span style={styles.label}>Clue {i + 1}</span>
                        <input
                          style={{ ...styles.input, width: "250px" }}
                          value={clue}
                          onChange={(e) => {
                            const newClues = [...clues];
                            newClues[i] = e.target.value;
                            setClues(newClues);
                          }}
                          disabled={isEnded}
                        />
                      </div>
                    ))}
                    <div style={styles.row}>
                      <span style={styles.label}>Answer</span>
                      <input
                        style={{ ...styles.input, width: "200px" }}
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        disabled={isEnded}
                      />
                    </div>
                  </>
                )}

                <div style={styles.row}>
                  <span style={styles.label}>Image</span>
                  <select
                    style={{ ...styles.input, width: "200px" }}
                    value={selectedImage}
                    onChange={(e) => setSelectedImage(e.target.value)}
                    disabled={isEnded}
                  >
                    <option value="">-- select --</option>
                    {images.map((img) => (
                      <option key={img} value={img}>
                        {img}
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  style={{
                    marginTop: "15px",
                    marginLeft: "140px",
                    border: "1px solid #80A0FF",
                    width: "250px",
                    height: "150px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ maxWidth: "100%", maxHeight: "100%" }}
                    />
                  ) : (
                    <span style={{ color: "#ccc" }}>
                      {selectedImage ? "Loading..." : "No Image Selected"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.rightPanel}>
              <div style={styles.allocationSection}>
                <div style={styles.allocHeader}>
                  <label>
                    <input
                      type="checkbox"
                      checked={allocSchoolsChecked}
                      onChange={(e) => setAllocSchoolsChecked(e.target.checked)}
                      disabled={isEnded}
                    />{" "}
                    Allocated to schools in
                  </label>
                </div>
                <div style={styles.scrollTable}>
                  <table width="100%" cellSpacing={0} cellPadding={0}>
                    <thead style={{ position: "sticky", top: 0 }}>
                      <tr style={{ ...styles.gridHeader }}>
                        <td
                          width="30px"
                          style={{
                            borderRight: "1px solid #a3c0e8",
                            padding: "3px 8px",
                          }}
                        ></td>
                        <td style={{ padding: "3px 8px" }}>Country</td>
                      </tr>
                    </thead>
                    <tbody>
                      {allCountries.map((c, i) => (
                        <tr key={c} style={styles.gridRow(i)}>
                          <td
                            width="30px"
                            style={{
                              borderRight: "1px solid #a3c0e8",
                              borderBottom: "1px solid #a3c0e8",
                              padding: "3px 8px",
                              textAlign: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedSchoolCountries.includes(c)}
                              onChange={() => toggleSchoolCountry(c)}
                              disabled={isEnded || !allocSchoolsChecked}
                            />
                          </td>
                          <td
                            style={{
                              borderBottom: "1px solid #a3c0e8",
                              padding: "3px 8px",
                              color: allocSchoolsChecked ? "black" : "gray",
                            }}
                          >
                            {c}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={styles.allocationSection}>
                <div style={styles.allocHeader}>
                  <label>
                    <input
                      type="checkbox"
                      checked={allocHomesChecked}
                      onChange={(e) => setAllocHomesChecked(e.target.checked)}
                      disabled={isEnded}
                    />{" "}
                    Allocated to home users
                  </label>
                </div>
                <div style={{ ...styles.scrollTable, height: "120px" }}>
                  <table width="100%" cellSpacing={0} cellPadding={0}>
                    <thead style={{ position: "sticky", top: 0 }}>
                      <tr style={{ ...styles.gridHeader }}>
                        <td
                          width="30px"
                          style={{
                            borderRight: "1px solid #a3c0e8",
                            borderBottom: "1px solid #a3c0e8",
                            padding: "3px 8px",
                          }}
                        ></td>
                        <td style={{ padding: "3px 8px" }}>Country</td>
                      </tr>
                    </thead>
                    <tbody>
                      {allCountries.map((c, i) => (
                        <tr key={c} style={styles.gridRow(i)}>
                          <td
                            width="30px"
                            style={{
                              borderRight: "1px solid #a3c0e8",
                              borderBottom: "1px solid #a3c0e8",
                              padding: "3px 8px",
                              textAlign: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedHomeCountries.includes(c)}
                              onChange={() => toggleHomeCountry(c)}
                              disabled={isEnded || !allocHomesChecked}
                            />
                          </td>
                          <td
                            style={{
                              borderRight: "1px solid #a3c0e8",
                              borderTop: "1px solid #a3c0e8",
                              padding: "3px 8px",
                              color: allocHomesChecked ? "black" : "gray",
                            }}
                          >
                            {c}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ ...styles.scrollTable, height: "150px" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "9pt",
                    }}
                  >
                    <thead style={{ position: "sticky", top: 0 }}>
                      <tr style={{ ...styles.gridHeader }}>
                        <th
                          align="left"
                          style={{
                            width: "30px",
                            borderRight: "1px solid #a3c0e8",
                            padding: "3px 8px",
                          }}
                        ></th>
                        <th
                          align="left"
                          style={{
                            borderRight: "1px solid #a3c0e8",
                            borderBottom: "1px solid #a3c0e8",
                            padding: "3px 8px",
                          }}
                        >
                          eDealer
                        </th>
                        <th
                          align="left"
                          style={{ width: "80px", padding: "3px 8px" }}
                        >
                          Country
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dealersList.map((d, i) => {
                        const isSelected = selectedDealerCodes.includes(
                          d.SchoolCode,
                        );
                        return (
                          <tr
                            key={`${d.Country}-${d.SchoolCode}`}
                            style={styles.gridRow(i)}
                          >
                            <td
                              style={{
                                borderRight: "1px solid #a3c0e8",
                                borderBottom: "1px solid #a3c0e8",
                                padding: "3px 8px",
                                textAlign: "center",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() =>
                                  toggleDealerSelection(d.SchoolCode)
                                }
                                disabled={isEnded || !allocHomesChecked}
                              />
                            </td>
                            <td
                              style={{
                                borderRight: "1px solid #a3c0e8",
                                borderBottom: "1px solid #a3c0e8",
                                padding: "3px 8px",
                                color: allocHomesChecked ? "black" : "gray",
                              }}
                            >
                              {d.Name}
                            </td>
                            <td
                              style={{
                                borderRight: "1px solid #a3c0e8",
                                borderBottom: "1px solid #a3c0e8",
                                padding: "3px 8px",
                                color: allocHomesChecked ? "black" : "gray",
                              }}
                            >
                              {d.Country}
                            </td>
                          </tr>
                        );
                      })}
                      {dealersList.length === 0 && (
                        <tr>
                          <td
                            colSpan={3}
                            style={{
                              padding: "5px",
                              color: "gray",
                              textAlign: "center",
                            }}
                          >
                            Check a country above to see eDealers
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <div style={{ marginBottom: '2px', fontWeight: 'bold', fontSize: '9pt' }}>Terms & Conditions</div>
                                    <textarea 
                                        style={{ ...styles.input, width: '60%', height: '80px' }}
                                        value={terms}
                                        onChange={e => setTerms(e.target.value)}
                                        disabled={isEnded}
                                    />
                                </div> */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginTop: "5px",
                    width: "100%",
                    backgroundColor: "#f0ffff",
                  }}
                >
                  {/* Label */}
                  <div
                    style={{
                      width: "180px",
                      fontWeight: "bold",
                      fontSize: "9pt",
                      paddingTop: "6px",
                    }}
                  >
                    Terms & Conditions
                  </div>

                  {/* Textarea */}
                  <textarea
                    style={{
                      ...styles.input,
                      flex: 1,
                      height: "80px",
                      resize: "none",
                    }}
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                    disabled={isEnded}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            {!isEnded && (
              <button onClick={handleSave} style={styles.footerBtn}>
                Save
              </button>
            )}
            <button onClick={handleCancel} style={styles.footerBtn}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EL_CompARD;
